import { type OperationVariables } from '@apollo/react-common';
import { type GraphQLTaggedNode } from 'react-relay';

import {
	captureOptimisticInteropWrites,
	createCacheSnapshotRestore,
	recordOptimisticInteropWrite,
	type OptimisticInteropWriteRollback,
} from './optimisticInteropWriteRollback.ts';

const queryObjectIds = new WeakMap<object, number>();
let nextQueryObjectId = 0;

export type OptimisticQueryWriteRollback = OptimisticInteropWriteRollback;
export const captureOptimisticQueryWrites: typeof captureOptimisticInteropWrites =
	captureOptimisticInteropWrites;

// Builds the query-specific restore point used by writeQueryInterop. Prefer a
// readable previous query result because replaying it goes through the original
// write path; fall back to whole-cache snapshots when the optimistic write is
// creating cache shape that could not previously be read as a complete query.
export function recordOptimisticQueryWriteRollback<
	TData,
	TVariables extends Record<string, unknown> = OperationVariables,
>({
	query,
	variables,
	callerPackage,
	readPreviousData,
	writePreviousData,
	writeData,
}: {
	query: GraphQLTaggedNode;
	variables?: TVariables;
	callerPackage?: string;
	readPreviousData: () => TData | null;
	writePreviousData: (data: TData) => void;
	writeData: () => void;
}): void {
	recordOptimisticInteropWrite({
		getKey: () => getOptimisticQueryWriteKey({ query, variables, callerPackage }),
		createRestore: () => {
			let previousData: TData | null;
			try {
				// Prefer the narrow restore path when the previous query result is
				// readable. Replaying the old result through writeQueryInterop keeps
				// rollback scoped to the exact query the optimistic update touched.
				previousData = readPreviousData();
			} catch {
				// Apollo Cache 1.x can throw for missing/partial query data. In that
				// case we still need rollback coverage, so fall back to a broader
				// cache snapshot instead of leaving the optimistic write untracked.
				return createCacheSnapshotRestore();
			}

			if (previousData == null) {
				// A null read means the optimistic write may create records or list
				// shape that did not previously exist. A whole-cache snapshot is the
				// safest rollback because there is no precise previous query result
				// to replay.
				return createCacheSnapshotRestore();
			}

			return () => {
				// Restore by going back through the original interop API rather than
				// mutating either cache directly. This preserves Apollo/Relay routing
				// and any normal write-side behavior owned by writeQueryInterop.
				writePreviousData(previousData);
			};
		},
		applyWrite: writeData,
	});
}

// Produces a stable key for deduping repeated writes to the same query inside a
// single optimistic update. Relay artifacts do not expose a simple public cache
// key here, so object identity plus variables/caller package is the local key.
function getOptimisticQueryWriteKey<
	TVariables extends Record<string, unknown> = OperationVariables,
>({
	query,
	variables,
	callerPackage,
}: {
	query: GraphQLTaggedNode;
	variables?: TVariables;
	callerPackage?: string;
}) {
	// Query artifacts are object identities at runtime. Use a WeakMap ID plus
	// variables/caller package so an optimistic mutation captures each query write
	// once without relying on Relay artifact internals.
	const queryObject = query as object;
	if (!queryObjectIds.has(queryObject)) {
		// Assign lazily so we do not retain artifact objects outside the WeakMap.
		queryObjectIds.set(queryObject, nextQueryObjectId++);
	}

	return JSON.stringify({
		query: queryObjectIds.get(queryObject),
		variables: variables ?? {},
		callerPackage: callerPackage ?? null,
	});
}
